import React, {
    useState, useRef, useCallback,
} from 'react';
import { authManager } from '../utils/auth';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_MIN_CHARS = 2;

export const memberDisplayName = (member) => [member.name, member.lastName].filter(Boolean).join(' ') || member.email;

// Shared by UploadLoyaltyReceipt.js and EditLoyaltyReceipt.js — a typeahead
// against GET /users/list. `initialMember` prefills the field already
// selected (the edit screen's current owner); leave it null for a blank
// picker (the upload screen). Note: /users/list wraps its payload under
// `result`, not `data` — utils/response.js's status.success() convention,
// different from this app's own loyalty admin endpoints. `required` only
// controls the asterisk; LoyaltyInsights.js uses it as an optional filter.
const MemberSearchField = ({
    initialMember = null, onSelect, disabled = false, label = 'Member', required = true,
}) => {
    const [searchTerm, setSearchTerm] = useState(
        initialMember ? `${memberDisplayName(initialMember)} (${initialMember.email})` : '',
    );
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [selectedMember, setSelectedMember] = useState(initialMember);
    const debounceRef = useRef(null);

    const searchMembers = useCallback(async (term) => {
        if (term.trim().length < SEARCH_MIN_CHARS) {
            setSearchResults([]);
            setSearchError('');
            return;
        }
        setSearching(true);
        setSearchError('');
        try {
            const params = new URLSearchParams({ search: term.trim(), limit: '10' });
            const res = await authManager.authenticatedFetch(`${Base_Url}/users/list?${params.toString()}`);
            const data = await res.json();
            if (!res.ok) {
                throw new Error(data?.error?.message || 'Could not search members. Please try again.');
            }
            setSearchResults(data?.result?.users || []);
        } catch (err) {
            setSearchResults([]);
            setSearchError(err.message || 'Could not search members. Please try again.');
        } finally {
            setSearching(false);
        }
    }, []);

    const handleSearchChange = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        setSelectedMember(null);
        onSelect(null);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => searchMembers(term), SEARCH_DEBOUNCE_MS);
    };

    const handleSelectMember = (member) => {
        setSelectedMember(member);
        setSearchTerm(`${memberDisplayName(member)} (${member.email})`);
        setSearchResults([]);
        setSearchError('');
        onSelect(member);
    };

    return (
        <div className="form-group row" style={{ position: 'relative' }}>
            <label className="col-12 col-md-4 col-form-label">{label}{required && <span className="f_red">*</span>}</label>
            <div className="col-12 col-md-8">
                <input
                    type="text"
                    className="form-control"
                    placeholder="Search by name or email"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    disabled={disabled}
                    autoComplete="off"
                />
                {searching && <small className="text-muted d-block mt-1">Searching…</small>}
                {searchError && <small className="text-danger d-block mt-1">{searchError}</small>}
                {!searching && !searchError && !selectedMember
                    && searchTerm.trim().length >= SEARCH_MIN_CHARS
                    && searchResults.length === 0 && (
                    <small className="text-muted d-block mt-1">No members found.</small>
                )}
                {searchResults.length > 0 && (
                    <ul className="list-group" style={{
                        position: 'absolute', zIndex: 5, width: '100%', maxHeight: '240px', overflowY: 'auto',
                    }}
                    >
                        {searchResults.map((member) => (
                            <li
                                key={member._id}
                                className="list-group-item list-group-item-action"
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleSelectMember(member)}
                            >
                                <strong>{memberDisplayName(member)}</strong>
                                <br />
                                <small className="text-muted">{member.email}</small>
                            </li>
                        ))}
                    </ul>
                )}
                {selectedMember && (
                    <small className="text-success d-block mt-1">
                        <i className="fa-solid fa-circle-check" /> Selected: {memberDisplayName(selectedMember)} ({selectedMember.email})
                    </small>
                )}
            </div>
        </div>
    );
};

export default MemberSearchField;
